import React, { useState, useEffect } from 'react';
import { ChevronDown, ArrowRight, Code, Globe } from 'lucide-react';
import Navbar from '../../layout/Navbar';
import Footer from '../../layout/Footer';
import { Link } from 'react-router-dom';

const WebDevelopment = () => {
    const [scrollY, setScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [showMore, setShowMore] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const observers = new Map();
        const elements = document.querySelectorAll('[data-animate]');

        elements.forEach((el, index) => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(prev => ({ ...prev, [index]: true }));
                    }
                },
                { threshold: 0.1 }
            );
            observer.observe(el);
            observers.set(index, observer);
        });

        return () => observers.forEach(observer => observer.disconnect());
    }, []);

    const stats = [
        { number: "10+", label: "Websites / Software Build" },
        { number: "10+", label: "Custom Platforms" },
        { number: "5+", label: "Technologies Mastered" },
        { number: "98%", label: "Uptime Guarantee" }
    ];

    const features = [
        { name: 'Responsive Design', desc: 'Mobile-first websites that look great on any device.' },
        { name: 'SEO Optimization', desc: 'Built-in SEO best practices for better search rankings.' },
        { name: 'Performance Tuning', desc: 'Fast-loading sites with optimized code and assets.' },
        { name: 'Security Features', desc: 'Robust protection against threats and data breaches.' },
        { name: 'CMS Integration', desc: 'Easy content management with popular CMS like WordPress or custom solutions.' },
        { name: 'API Development', desc: 'Seamless integration with third-party services and APIs.' },
    ];

    const technologies = [
        { name: "React.js", desc: "Dynamic single-page applications." },
        { name: "Node.js", desc: "Scalable backend services." },
        { name: "Tailwind CSS", desc: "Rapid UI development." },
        { name: "AWS", desc: "Cloud hosting and deployment." },
        { name: "MySQL", desc: "Flexible database solutions." },
        { name: "MongoDB", desc: "Efficient data querying." },
    ];

    return (
        <div className="min-h-screen bg-white text-gray-800">
            <Navbar />

            {/* Hero Section */}
            <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto text-center">
                    <div
                        className="animate-fade-in-up"
                        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
                    >
                        <h1 className="text-5xl md:text-7xl font-bold mt-16 text-gray-800 leading-tight">
                            Web
                            <br />
                            <span className="text-green-500 inline-block animate-pulse">Development</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed capitalize">
                            Crafting <span className="text-green-500 font-semibold">high-performance, scalable web solutions</span> that drive business success and user engagement.
                        </p>
                    </div>

                    {/* Scroll Indicator */}
                    <div className="animate-bounce mt-16">
                        <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-gray-900" data-animate>
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-white">
                                <div className="text-4xl md:text-5xl font-bold mb-2 text-green-400 animate-pulse">{stat.number}</div>
                                <div className="text-gray-300 text-lg">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Service Overview Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-200 ${isVisible[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Our Web <span className="text-green-500">Development</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto capitalize">
                            At OneHexa Tech, we specialize in building <span className="text-green-500 font-semibold">modern, user-centric websites</span> using cutting-edge technologies like React, Node.js, and Tailwind CSS to deliver exceptional digital experiences.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Approach */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <Code className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Approach</h3>
                            <p className="text-gray-600 leading-relaxed">
                                We follow agile methodologies to create <span className="text-green-500 font-semibold">custom web solutions</span> tailored to your business needs, ensuring scalability and maintainability.
                            </p>
                        </div>

                        {/* Benefits */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <Globe className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Key Benefits</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Enhanced online presence, improved user experience, and <span className="text-green-500 font-semibold">increased conversion rates</span> through our expert web development services.
                            </p>
                        </div>

                        {/* Process */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <ArrowRight className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Process</h3>
                            <ul className="list-disc list-inside text-gray-600 space-y-2">
                                <li>Discovery & Planning</li>
                                <li>Design & Prototyping</li>
                                <li>Development & Testing</li>
                                <li>Deployment & Support</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-animate>
                <div className={`max-w-7xl mx-auto text-center transition-all duration-1000 delay-250 ${isVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                        Key <span className="text-green-500">Features</span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
                        Discover the <span className="text-green-500 font-semibold">core features</span> that make our web development services stand out.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {features.map((feature, idx) => (
                            <div key={idx} className="bg-gray-50 p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border hover:border-green-300">
                                <Code className="w-10 h-10 text-green-500 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{feature.name}</h3>
                                <p className="text-gray-600">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies Section with Show More */}
            <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-300 ${isVisible[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Our <span className="text-green-500">Technologies</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Cutting-edge tools and frameworks we use for web development.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 cursor-pointer">
                        {(showMore ? technologies : technologies.slice(0, 6)).map((tech, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-6 text-center hover:shadow-xl transform hover:scale-105 transition-all duration-300 border border-gray-100 group hover:border-green-200"
                            >
                                <h3 className="text-lg font-bold text-gray-600 mb-1 group-hover:text-green-600 transition-colors duration-300">{tech.name}</h3>
                                <p className="text-sm text-gray-600">{tech.role}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gray-900 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 delay-400 ${isVisible[4] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Build Your <span className="text-green-400">Website?</span>
                    </h2>
                    <p className="text-xl text-gray-300 mb-8">
                        Partner with OneHexa Tech for top-tier web development services.
                    </p>
                    <Link to="/contact">
                        <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                            Get Started
                        </button>
                    </Link>
                </div>
            </section>

            <Footer />

            <style jsx>{`
                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 1s ease-out;
                }
            `}</style>
        </div>
    );
};

export default WebDevelopment;